import { useCallback, useEffect, useState } from 'react';
import type {
  DetectionHit,
  ProjectPause,
  ProjectSpend,
  ProvenanceRecord,
  StopSwitch,
} from '@reef/schema';
import { api } from '../api.ts';

/**
 * Operating the factory, rather than building more of it.
 *
 * Four controls on one screen, and the grouping is the argument: they are what
 * somebody reaches for when the factory is doing something they did not want,
 * and at that moment the last thing they should be doing is working out which
 * screen owns which verb. Stopping it, seeing what it is spending, seeing what
 * was noticed, and seeing what it shipped are one job.
 *
 * The stop switch is first and stays first. Everything below it is diagnosis;
 * that is the only thing here that changes what the factory is doing right now.
 */
export function OperationsView({ isAdmin }: { isAdmin: boolean }) {
  const [switches, setSwitches] = useState<StopSwitch[]>([]);
  const [projects, setProjects] = useState<ProjectSpend[]>([]);
  const [pauses, setPauses] = useState<ProjectPause[]>([]);
  const [detections, setDetections] = useState<DetectionHit[]>([]);
  const [provenance, setProvenance] = useState<ProvenanceRecord[]>([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [stop, budgets, paused, hits, shipped] = await Promise.all([
        api.stopSwitches(),
        api.budgets(),
        api.projectPauses(),
        api.detections(),
        api.provenance(),
      ]);
      setSwitches(stop.live);
      setProjects(budgets.projects);
      setPauses(paused.pauses);
      setDetections(hits.detections);
      setProvenance(shipped.provenance);
    } catch (caught) {
      setError((caught as Error).message);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const global = switches.find((entry) => entry.scope === null);
  const unacknowledged = detections.filter((hit) => !hit.acknowledgedAt);

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Operations</span>
        </span>
        <span className="spacer" />
        {unacknowledged.length > 0 && (
          <span className="chip warn">{unacknowledged.length} unacknowledged</span>
        )}
        <button onClick={() => void refresh()} disabled={busy}>
          Refresh
        </button>
      </header>

      <div className="studio-column wide">
        {error && <div className="banner danger">{error}</div>}

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Stop switch</h2>
            {global && <span className="chip warn">the factory is stopped</span>}
          </div>
          <div className="studio-card-body">
            {global ? (
              <>
                <div className="banner warn">
                  <strong>{global.reason}</strong>
                  <p style={{ margin: '0.25rem 0 0' }}>
                    Engaged by {global.engagedBy ?? 'an operator'} at{' '}
                    {new Date(global.engagedAt).toLocaleString()}. Triggers, watchers, schedules and
                    new runs are all refused while this is on.
                  </p>
                </div>
                {isAdmin && (
                  <button onClick={() => void act(() => api.releaseStop(global.id))} disabled={busy}>
                    Release
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="hint" style={{ marginTop: 0 }}>
                  Halts triggers, watchers, schedules and new runs across the deployment. Runs
                  already in flight stop at their next durable checkpoint, because killing a step
                  mid-call is how you get a pull request nobody knows was opened.
                </p>
                {isAdmin ? (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="why are you stopping it?"
                      aria-label="Reason"
                      style={{ flex: 1 }}
                    />
                    {/*
                      A reason is required rather than encouraged. An engaged
                      switch with no reason is one nobody dares release, which
                      is how a stopped factory stays stopped for a week.
                    */}
                    <button
                      onClick={() => void act(() => api.engageStop(reason.trim(), null))}
                      disabled={busy || reason.trim().length < 3}
                    >
                      Stop the factory
                    </button>
                  </div>
                ) : (
                  <p className="hint">Stopping the factory is an admin action.</p>
                )}
              </>
            )}

            {switches.filter((entry) => entry.scope !== null).length > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>Workflow</th>
                    <th>Reason</th>
                    <th>Engaged by</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {switches
                    .filter((entry) => entry.scope !== null)
                    .map((entry) => (
                      <tr key={entry.id}>
                        <td>{entry.scope}</td>
                        <td>{entry.reason}</td>
                        <td>{entry.engagedBy ?? '—'}</td>
                        <td>
                          {isAdmin && (
                            <button
                              className="linkish"
                              onClick={() => void act(() => api.releaseStop(entry.id))}
                            >
                              release
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Project spend</h2>
            <span className="chip">rolling 24 hours</span>
          </div>
          <div className="studio-card-body">
            {projects.length === 0 ? (
              <p className="hint" style={{ marginTop: 0 }}>
                Nothing has spent anything in the window.
              </p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Spend</th>
                    <th>Usually</th>
                    <th>Ceiling</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <tr key={project.workflowId}>
                      <td>
                        {project.workflowId}
                        {project.anomalous && <span className="chip warn">anomalous</span>}
                        {project.paused && <span className="chip warn">paused</span>}
                      </td>
                      <td>${(project.spendCents / 100).toFixed(2)}</td>
                      {/*
                        Spend is unreadable on its own: four pounds means nothing
                        until you know whether this project normally spends forty
                        pence or forty pounds.
                      */}
                      <td>
                        {project.trailingDailyCents === null
                          ? '—'
                          : `$${(project.trailingDailyCents / 100).toFixed(2)}/day`}
                      </td>
                      <td>
                        {project.budget
                          ? `$${(project.budget.dailyCents / 100).toFixed(2)} · ${project.budget.action}`
                          : 'none'}
                      </td>
                      <td>
                        {project.paused && isAdmin && (
                          <button
                            className="linkish"
                            onClick={() => void act(() => api.resumeProject(project.workflowId))}
                          >
                            resume
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {pauses.length > 0 && (
              <p className="hint">
                {pauses.length} project{pauses.length === 1 ? '' : 's'} paused by a ceiling. A
                project paused three times this month is a ceiling that is wrong, not a project.
              </p>
            )}
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Detections</h2>
            {detections.length > 0 && <span className="chip">{detections.length} recent</span>}
          </div>
          <div className="studio-card-body">
            {detections.length === 0 ? (
              <p className="hint" style={{ marginTop: 0 }}>
                Nothing has fired. The rules are deliberately few — a channel people mute is worse
                than no channel, because everybody believes it is working.
              </p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Rule</th>
                    <th>What</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detections.map((hit) => (
                    <tr key={hit.id}>
                      <td>{new Date(hit.detectedAt).toLocaleTimeString()}</td>
                      <td>
                        <span className={`chip ${hit.severity === 'critical' ? 'warn' : ''}`}>
                          {hit.severity}
                        </span>
                        {hit.rule}
                      </td>
                      <td>
                        {hit.summary}
                        {!hit.notifiedAt && (
                          <div className="tile-note">not yet delivered to its destination</div>
                        )}
                      </td>
                      <td>
                        {hit.acknowledgedAt ? (
                          <span className="tile-note">acknowledged</span>
                        ) : (
                          <button
                            className="linkish"
                            onClick={() => void act(() => api.acknowledgeDetection(hit.id))}
                          >
                            acknowledge
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>What the factory shipped</h2>
          </div>
          <div className="studio-card-body">
            {provenance.length === 0 ? (
              <p className="hint" style={{ marginTop: 0 }}>
                No pull request has been recorded yet. Provenance is written when a step reports
                one, so a pull request opened by hand has none.
              </p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Pull request</th>
                    <th>Workflow</th>
                    <th>Model</th>
                    <th>Under which instructions</th>
                  </tr>
                </thead>
                <tbody>
                  {provenance.map((record) => (
                    <tr key={record.id}>
                      <td>
                        {record.repo}#{record.pullRequest}
                      </td>
                      <td>{record.workflowId}</td>
                      <td>{record.model}</td>
                      {/*
                        The column the whole record exists for: six weeks later,
                        "which version of its instructions" is answerable from
                        here and from nowhere else.
                      */}
                      <td>{(record.skills ?? []).join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
